import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { AlertCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import useAuthStore from "@/store/useAuthStore";
import GoogleIcon from "@/assets/icons/google-icon.svg";
import { Alert, AlertDescription } from "@/components/ui/alert";

import { ApiService } from "@/services/api-service";

import { seo } from "@/lib/seo";
import { SERVER_API_PUBLIC } from "@/utils/constants";

export const Route = createFileRoute("/auth/_layout/login")({
  component: Login,
  ssr: true,
  head: () => {
    const title = "Login | Inkriv";
    const description =
      "Log into your Inkriv account and start writing smarter with AI.";
    const image =
      "https://res.cloudinary.com/dtcejk6h3/image/upload/v1747040895/og-image_ztxfrr.png";

    return {
      title,
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        ...seo({ title, description, image }),
      ],
    };
  },
});

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const authRes = await ApiService.login({
        email,
        password,
      });

      console.log(authRes.authTokens);

      login(authRes.user, authRes.authTokens);
      navigate({
        to: "/app",
      });
    } catch (err) {
      const errMsg = (err as any)?.response?.data?.message || "Login Failed";
      setError(errMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const { error: queryError } = Route.useSearch();
  console.log(queryError);

  return (
    <>
      <div className="max-w-md w-full">
        <Card className=" shadow-2xl rounded-4xl bg-neutral-50">
          <Link to="/" className="flex justify-center mb-1">
            <img src="/logo/inkriv-logo-dark.svg" alt="logo" className="h-12" />
          </Link>
          <CardContent className="pt-6">
            <Button
              variant="outline"
              className="w-full mb-4 flex items-center justify-center gap-2"
              onClick={() => {
                window.location.href = `${SERVER_API_PUBLIC}/auth/google`;
              }}
              disabled={isLoading}
            >
              <span className="flex items-center justify-center gap-x-2 flex-nowrap font-satoshi">
                <img src={GoogleIcon} alt="google icon" className="w-4 h-4" />
                Continue with Google
              </span>
            </Button>

            <div className="relative flex items-center justify-center my-8">
              <div className="border-t border-gray-200 w-full dark:border-gray-900"></div>
              <span className="bg-neutral-50 dark:bg-neutral-950 px-2 text-sm text-gray-500 dark:text-gray-300 absolute">
                OR
              </span>
            </div>

            <form onSubmit={handleLogin} className="space-y-4 mb-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-1 font-satoshi"
                >
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-neutral-25 dark:bg-neutral-950 pr-10 text-xs placeholder:text-[12px] sm:placeholder:text-xs font-satoshi"
                />
              </div>
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium mb-1 font-satoshi"
                >
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-neutral-25 dark:bg-neutral-950 pr-10 text-xs placeholder:text-[12px] sm:placeholder:text-xs font-satoshi"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-4 transform -translate-y-1/2"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                  <Link
                    to="/auth/forgot-password"
                    className="text-xs mt-5 underline text-secondary-700 font-satoshi"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2 font-satoshi"
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Login"
                )}
              </Button>
            </form>
            <div className="flex flex-col">
              <Link
                to="/auth/signup"
                className="underline text-secondary-700 font-satoshi"
              >
                Dont have an account? Sign up
              </Link>
            </div>
          </CardContent>
          {error && (
            <CardFooter className="flex flex-col pt-0 relative max-w-lg  rounded-b-2xl">
              {error && (
                <Alert
                  variant="destructive"
                  className="mt-1 p-2 mb-1 flex items-center justify-center"
                >
                  <AlertCircle size={12} />
                  <AlertDescription className="text-sm font-satoshi">
                    {error}
                  </AlertDescription>
                </Alert>
              )}
            </CardFooter>
          )}

          {queryError === "signup_disabled" && (
            <CardFooter className="flex flex-col pt-0 relative max-w-lg  rounded-b-2xl">
              <Alert
                variant="destructive"
                className="mt-1 p-2 mb-1 flex items-center justify-center"
              >
                <AlertCircle size={12} />
                <AlertDescription className="text-sm font-satoshi text-green-200 ">
                  {"Application not accessible at the moment"}
                </AlertDescription>
              </Alert>
            </CardFooter>
          )}
        </Card>
      </div>
    </>
  );
}
